import React, { useState, useEffect } from "react";
import { sortByLanguageList } from "../../../../utils/utils";
import {
  DEFAULT_COLUMNS,
  DEFAULT_LANGUAGE_CODE,
  DEFAULT_PART_COLUMNS,
  DEFAULT_PART_COLUMN,
  PROVIDE_COLUMN_NAMES,
  MAX_ADDITIONAL_COLS,
  addCol1,
} from "../../../../utils/surveyLiterals";
import { ReactComponent as DeleteIcon } from "@deere/ux.brand-foundations/icons/delete.svg";
import { ReactComponent as EditIcon } from "@deere/ux.brand-foundations/icons/edit.svg";
import { Grid, Typography, TextField, Button, Box, Table, TableHead, TableRow, TableCell, TableBody } from "@deere/fuel-react";
import { ERR_EXISTING_COL } from "../../../../utils/errorLiterals";

const PartTableColumns = ({ payloadData, setPayloadData, languageList }) => {
  const [isEditing, setIsEditing] = useState({ colName: "", status: false });
  const [newColNames, setNewColNames] = useState({});
  const [existingColError, setExistingColError] = useState(false);
  const [translationLanguages, setTranslationLanguages] = useState(sortByLanguageList(payloadData.language, languageList));

  useEffect(() => {
    if (payloadData?.language?.length > 0) {
      if (payloadData?.defaultColumns?.length === 0) {
        const defaultCols = DEFAULT_COLUMNS.map((col) => {
          const translations = col.translations.filter((trn) => payloadData.language.some((lng) => lng.languageCd === trn.locale));
          return { ...col, translations: translations };
        });
        setPayloadData((prevState) => ({ ...prevState, defaultColumns: defaultCols }));
      } else {
        setPayloadData((prevState) => {
          const newDefCols = prevState.defaultColumns.map((col) => {
            const colIdx = DEFAULT_COLUMNS.findIndex((dCol) => dCol.columnNames === col.columnNames);
            if (colIdx > -1) {
              const translations = DEFAULT_COLUMNS[colIdx].translations.filter((trn) =>
                payloadData.language.some((lng) => lng.languageCd === trn.locale),
              );
              return { ...col, translations: translations };
            }
            return col;
          });
          return { ...prevState, defaultColumns: newDefCols };
        });
      }
    }
  }, [payloadData?.language]);

  const showIcons = (colName) => {
    const additionallyAddedColumns = payloadData.defaultColumns.filter((col) => !DEFAULT_PART_COLUMNS.includes(col.columnNames));
    const index = additionallyAddedColumns.findIndex((col) => col.columnNames === colName);
    return index > -1;
  };

  const deleteAdditionalColumn = (colName) => {
    setPayloadData((prevState) => {
      const newDefaultColumns = prevState.defaultColumns.filter((col) => col.columnNames !== colName);
      newDefaultColumns.forEach((col) => {
        if (col.defaultColumnMapping && col.defaultColumnMapping !== "") {
          col.defaultColumnMapping = addCol1; //resetting the mapping for existing additional columns to avoid any mismatch after deletion of a column in between
        }
      });
      return { ...prevState, defaultColumns: newDefaultColumns };
    });
    setNewColNames({});
    setIsEditing({ colName: "", status: false });
  };

  const editAdditionalColumn = (colName) => {
    const colIndex = payloadData?.defaultColumns?.findIndex((col) => col.columnNames === colName);
    if (colIndex === -1) return;
    const colData = payloadData.defaultColumns[colIndex];
    const colNames = { [DEFAULT_LANGUAGE_CODE]: colData.columnNames };
    translationLanguages.forEach((trn) => {
      if (trn.languageCd !== DEFAULT_LANGUAGE_CODE) {
        colNames[trn.languageCd] = colData?.translations?.find((t) => t.locale === trn.languageCd)?.text || "";
      }
    });
    setNewColNames(colNames);
    setIsEditing({ colName, status: true });
  };

  const isAddDisabled = () => {
    return translationLanguages.some((lang) => {
      const value = newColNames[lang.languageCd];
      return typeof value !== "string" || value.trim().length === 0;
    });
  };

  const isDisableNewCol = () => {
    if (isEditing.status) return false;
    if (payloadData && payloadData.defaultColumns) {
      const additionallyAddedColumns = payloadData.defaultColumns.filter((col) => !DEFAULT_PART_COLUMNS.includes(col.columnNames));
      return additionallyAddedColumns.length === MAX_ADDITIONAL_COLS * 2; //Each additional column is added for both NonDeere and Deere
    }
    return true;
  };

  const addNewColHandler = () => {
    if (payloadData?.defaultColumns?.some((col) => col.columnNames === newColNames[DEFAULT_LANGUAGE_CODE])) {
      setExistingColError(true);
      setNewColNames({});
      return true;
    }
    const translations = translationLanguages
      .filter((lang) => lang.languageCd !== DEFAULT_LANGUAGE_CODE)
      .map((lang) => ({ locale: lang.languageCd, language: lang.text, text: newColNames[lang.languageCd] }));
    const additionallyAddedColumns = payloadData.defaultColumns.filter((col) => !DEFAULT_PART_COLUMNS.includes(col.columnNames));
    const colNumber = additionallyAddedColumns.length === 0 ? "1" : "2";
    const newColumn = [
      {
        columnNames: newColNames[DEFAULT_LANGUAGE_CODE],
        isNonDeere: false,
        translations: translations,
        defaultColumnMapping: `additional_column_${colNumber}`,
      },
      {
        columnNames: newColNames[DEFAULT_LANGUAGE_CODE],
        isNonDeere: true,
        translations: translations,
        defaultColumnMapping: `additional_column_${colNumber}`,
      },
    ];
    const newDefaultColumns = [...payloadData.defaultColumns, ...newColumn.flat()];
    setPayloadData((prevState) => ({ ...prevState, defaultColumns: newDefaultColumns }));
    setNewColNames({});
  };

  const updateColHandler = () => {
    const matchingColumns = payloadData?.defaultColumns?.filter((col) => col.columnNames !== isEditing.colName);
    if (matchingColumns.some((col) => col.columnNames === newColNames[DEFAULT_LANGUAGE_CODE])) {
      setExistingColError(true);
      setNewColNames({});
      setIsEditing({ colName: "", status: false });
      return false;
    }
    setPayloadData((prevState) => {
      const translations = translationLanguages
        .filter((lang) => lang.languageCd !== DEFAULT_LANGUAGE_CODE)
        .map((lang) => ({ locale: lang.languageCd, language: lang.text, text: newColNames[lang.languageCd] }));
      const updatedColumns = [...prevState.defaultColumns];
      updatedColumns.forEach((col) => {
        if (col.columnNames === isEditing.colName) {
          col.columnNames = newColNames[DEFAULT_LANGUAGE_CODE];
          col.translations = translations;
        }
      });
      return { ...prevState, defaultColumns: updatedColumns };
    });
    setNewColNames({});
    setIsEditing({ colName: "", status: false });
  };

  const isGreyedOut = (colName) => {
    if (isEditing.status && isEditing.colName === colName) {
      return { color: "#bababa" };
    }
    return {};
  };

  if (translationLanguages.length === 0) return null;
  return (
    <Box style={{ border: "1px solid #e0e0e0", borderRadius: "4px", padding: "16px", marginTop: "16px" }}>
      <Typography size="md" fontWeight={600}>
        {DEFAULT_PART_COLUMN}
      </Typography>
      {payloadData.defaultColumns &&
        payloadData.defaultColumns
          .filter((column) => !column.isNonDeere)
          .map((col, index) => (
            <React.Fragment key={col.columnNames}>
              <Typography
                size="sm"
                ml={index > 0 ? 4 : 0}
                title={col.columnNames}
                style={{ ...isGreyedOut(col.columnNames), display: "inline" }}
              >{`${index + 1}.${col.columnNames}`}</Typography>
              {showIcons(col.columnNames) && (
                <>
                  <EditIcon
                    title="Edit"
                    height={18}
                    width={18}
                    fill={isGreyedOut(col.columnNames).color || "#6b6b6b"}
                    style={{ cursor: "pointer", marginLeft: "8px" }}
                    onClick={() => editAdditionalColumn(col.columnNames)}
                  />
                  <DeleteIcon
                    title="Delete"
                    height={18}
                    width={18}
                    fill={isGreyedOut(col.columnNames).color || "#6b6b6b"}
                    style={{ cursor: "pointer", marginLeft: "8px" }}
                    onClick={() => deleteAdditionalColumn(col.columnNames)}
                  />
                </>
              )}
            </React.Fragment>
          ))}
      <Grid container mt={2}>
        <Grid item xs={10}>
          <Table size="small">
            <TableHead sx={{ border: "none" }}>
              <TableRow>
                <TableCell width="80%">Column Name</TableCell>
                <TableCell width="20%">Language</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {translationLanguages.map((lng) => (
                <TableRow key={lng.languageCd} sx={{ border: "none!important" }}>
                  <TableCell>
                    <TextField
                      required
                      placeholder="Please provide the column name here..."
                      value={newColNames[lng.languageCd] || ""}
                      disabled={isDisableNewCol()}
                      onChange={(e) => {
                        setExistingColError(false);
                        setNewColNames((prevState) => ({ ...prevState, [lng.languageCd]: e.target.value }));
                      }}
                    />
                  </TableCell>
                  <TableCell>{lng.text}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Grid>
        <Grid item xs={2}>
          <Button
            onClick={isEditing.status ? updateColHandler : addNewColHandler}
            disabled={isAddDisabled() || isDisableNewCol()}
            title={isAddDisabled() && PROVIDE_COLUMN_NAMES}
          >
            {isEditing.status ? "Update" : "Add New Column"}
          </Button>
        </Grid>
      </Grid>
      <Box minHeight="24px" mt={1}>
        <Typography variant="subtitle2" color="#C21020" ml={4} style={{ visibility: existingColError ? "visible" : "hidden" }}>
          {ERR_EXISTING_COL}
        </Typography>
      </Box>
    </Box>
  );
};

export default PartTableColumns;
